import { useEffect, useState, useContext, useMemo } from 'react'
import { Delete, Edit, PollOutlined } from '@mui/icons-material'
import { TableCell, TableRow, Checkbox, Tooltip, Fab, CircularProgress } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import { Workflow as HeaderIcon } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Paper } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { targetContext, modalContext } from '@contexts'
import { colors } from '@global/constants'
import { useQueryString } from '@global/hooks'
import { api } from 'api'
import { getCellContent } from 'functions'
import { CvsTable } from '../../../components/table/cvs-table'
import { EnhancedTableToolbar } from '../../../components/table/enhanced-table-toolbar'
import * as SnackbarActions from '../../../global/snackbar/snackbar.actions'
import { Fuzzy } from '../../../utils/fuse-search'
import { t } from '@lingui/core/macro'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  textField: {
    width: 200,
  },
}))

export const Targets = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const classes = useStyles()
  const ModalContext = useContext(modalContext)
  const { byId, fetchListTargets, updateTargets } = useContext(targetContext)
  const [loaded, setLoaded] = useState(false)
  const [selected, setSelected] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const targets = useMemo(() => Object.keys(byId).map(id => byId[id]), [byId])
  const [displayTargets, setDisplayTargets] = useState(targets)
  const { setUrl, setStateFromQuery } = useQueryString(searchTerm, setSearchTerm)

  const columns = [
    { label: 'Name', width: '25%', lookup: 'name' },
    { label: 'Type', width: '25%', lookup: 'type' },
    { label: 'Multi Targets', width: '15%', lookup: 'multi_targets_count' },
    { label: 'Description', width: '35%', lookup: 'nickname' },
  ]

  useEffect(() => {
    setDisplayTargets(targets)
  }, [targets])

  useEffect(() => {
    setLoaded(false)
    fetchListTargets().then(updatedTargets => {
      if (history.location.search) {
        setStateFromQuery(term => {
          setSearchTerm(term)
          setDisplayTargets(fuzzySearch(term, updatedTargets))
        })
      }
      setLoaded(true)
    })
  }, [fetchListTargets])

  const filterChanged = search => {
    if (!search) {
      setDisplayTargets(targets)
      setSearchTerm('')
      setUrl()
    } else {
      setDisplayTargets(fuzzySearch(search))
      setUrl({ q: search })
    }
  }

  const fuzzySearch = (search, targs = targets) => {
    const fuzzy = new Fuzzy(targs, { keys: ['name'] })
    return fuzzy.search(search)
  }

  const editItems = () => {
    navigateToEdit({ id: Object.keys(selected)[0] })
  }

  const addItem = () => {
    history.push('/admin/targets/new')
  }

  const navigateToEdit = row => {
    history.push(`/admin/targets/${row.id}`)
  }

  const deleteItems = payload => {
    api
      .post('/api/v1/account/targets/bulk/delete', {
        target_ids: Object.keys(payload).filter(p => payload[p]),
      })
      .then(() => {
        const filteredTargets = targets.filter(t => !payload[t.id])
        updateTargets(filteredTargets)
        setSelected({})
        dispatch(
          SnackbarActions.showSnackbar({
            message: t`Target deleted successfully`,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(() => {
        dispatch(
          SnackbarActions.showSnackbar({
            message: t`Target failed to delete`,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }

  const deleteItemsModal = () => {
    ModalContext.displayModal({
      title: t`Are you sure you want to delete these Targets`,
      description: t`Targets are an important part of the Claravine experience and your data may depend on them. Please be sure this is what you'd like to do as this action can't be undone.`,
      confirm: () => {
        return () => {
          deleteItems(selected)
        }
      },
    })
  }

  const numSelected = Object.values(selected).filter(val => !!val).length

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ overflow: 'hidden' }}
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Targets`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`Use your connected accounts to create specific interactions within integrations`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <Paper elevation={2} className={classes.paper}>
        {loaded ? (
          <div className={classes.table} style={{ height: 'calc(100% - 145px)' }}>
            <EnhancedTableToolbar
              term={searchTerm}
              numSelected={numSelected}
              filterChanged={filterChanged}
              addItem={addItem}
              editItems={editItems}
              deleteItems={deleteItems}
              noAdd={false}
              blockMultipleEdit={false}
            >
              <Tooltip title={t`Edit`}>
                <Fab
                  color="primary"
                  style={{ color: 'white', marginRight: '10px' }}
                  size="small"
                  aria-label="Add"
                  className={classes.fab}
                  disabled={numSelected > 1}
                  onClick={editItems}
                >
                  <Edit />
                </Fab>
              </Tooltip>
              <Tooltip
                title={t`Delete`}
                style={{ backgroundColor: colors.red.light, color: 'white' }}
              >
                <Fab
                  color="inherit"
                  size="small"
                  aria-label="Add"
                  className={classes.fab}
                  onClick={deleteItemsModal}
                >
                  <Delete />
                </Fab>
              </Tooltip>
            </EnhancedTableToolbar>
            <div style={{ height: 'calc(100% - 64px)' }}>
              <CvsTable
                columns={columns}
                rows={displayTargets}
                orderBy="name"
                sortOrder="asc"
                keyField="id"
                noAdd={false}
                addItem={addItem}
                noPagination={true}
                style={{ maxWidth: '100%' }}
                multiSelect={true}
                emptyStateMessage={{
                  icon: (
                    <Fab
                      style={{
                        height: '65px',
                        width: '65px',
                        color: colors.gray.main,
                        cursor: 'auto',
                      }}
                      disabled
                    >
                      <PollOutlined style={{ fontSize: '34px', color: colors.blue.smokey }} />
                    </Fab>
                  ),
                  title: t`No targets found.`,
                  description: t`Try a different search or add a new target.`,
                }}
                selectAll={val => {
                  setSelected(
                    displayTargets.reduce(
                      (memo, item) => ({
                        ...memo,
                        [item.id]: val,
                      }),
                      {},
                    ),
                  )
                }}
              >
                {({ row }) => {
                  return (
                    <TableRow
                      hover
                      onClick={() => {
                        numSelected
                          ? setSelected({
                              ...selected,
                              [row.id]: !selected[row.id],
                            })
                          : navigateToEdit(row)
                      }}
                      tabIndex={-1}
                      key={row.id}
                      style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          onClick={e => {
                            e.stopPropagation()
                            setSelected({
                              ...selected,
                              [row.id]: !selected[row.id],
                            })
                          }}
                          checked={selected[row.id]}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.name}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.connector.connector_type.name}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.multi_targets_count}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getCellContent('text', row.nickname)}
                      </TableCell>
                    </TableRow>
                  )
                }}
              </CvsTable>
            </div>
          </div>
        ) : (
          <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress color="primary" />
          </div>
        )}
      </Paper>
    </motion.div>
  )
}
